import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/i18n/I18nProvider";
import { toast } from "sonner";

/**
 * Returns a function `requireAuth(action, labelKey?)` that:
 * - If logged in: runs `action()`
 * - If guest: shows localized toast + redirects to /auth?next=<current>
 *
 * `labelKey` should be a translation key under `auth.action.*`
 * (e.g. "sell", "cart", "post", "like", "comment", "events", "continue").
 * For backward compatibility, a raw label string is also accepted.
 */
export function useRequireAuth() {
  const { user } = useAuth();
  const { t } = useI18n();
  const nav = useNavigate();
  const loc = useLocation();

  return (action, labelKey = "continue") => {
    if (user) {
      action();
      return true;
    }
    // Try i18n first; fall back to literal label
    const i18nKey = `auth.action.${labelKey}`;
    const localizedAction = t(i18nKey);
    const actionText = localizedAction === i18nKey ? labelKey : localizedAction;
    toast.info(t("auth.signin_to", { action: actionText }));
    nav(`/auth?next=${encodeURIComponent(loc.pathname)}`);
    return false;
  };
}
